/* eslint-disable react/no-multi-comp */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Bem from 'bemHelper';
import classNames from 'classnames';
// import InfiniteTreeProto from 'infinite-tree';
import InfiniteTree from 'react-infinite-tree';
import each from 'lodash/each';
import isEqual from 'lodash/isEqual';
import apiDecorator from 'api/api-decorator';
import ApiError from 'errors/apiError';
import { getErrorMessage } from 'api/api-errors';
import { subscribe, unsubscribe } from 'helpers/global-events';
import { Checkbox } from 'containers/form';
import sensor from 'components/sensor';
import Button from 'components/button';
import { iconTypes } from 'components/icon';

import 'styles/base/components/dynamic-tree/_tree.scss';

const classes = new Bem('treeList');

/**
 * @deprecated
 * Patch for scroll to center, not to top
 * @param value
 * @return {number}
 */
/* InfiniteTreeProto.prototype.scrollTop = function(value) { // eslint-disable-line
  if (!this.scrollElement) {
    return 0;
  }
  if (value !== undefined) {
    const firstChild = this.contentElement.firstChild;
    const height = this.scrollElement.clientHeight;
    const elHeight = firstChild ? firstChild.clientHeight : 0;
    this.scrollElement.scrollTop = Number((value - (height / 2)) + (elHeight / 2));
  }
  return this.scrollElement.scrollTop;
}; */

/**
 * Tree component. Has two modes. If "data" prop
 * defined - render this data statically, if not
 * defined - rrequest a data for each opening node
 * For details - see prop-types
 *
 * Possible control data values:
 *   * $loadOnDemand true|false - Dynamically load item when it opens
 */

@sensor
@apiDecorator
class Tree extends PureComponent {
  static propTypes = {
    className: PropTypes.string,
    // Data provider
    dataProvider: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

    // Parent criteria for data provider - dynamic tree
    providerParentCriteria: PropTypes.string,

    // Data provider criterias
    query: PropTypes.shape({}),

    // Parent value for dynamic tree
    parent: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]),

    // ID field
    idField: PropTypes.string.isRequired,

    // Parent field
    parentField: PropTypes.string.isRequired, // eslint-disable-line

    // Field, that contains tree item title (hint)
    titleField: PropTypes.string,

    // Field, which contains children
    childrenField: PropTypes.string.isRequired,

    // Preloaded data (for static trees)
    data: PropTypes.any, // eslint-disable-line

    // Do not send query (send only parent) when children request
    // in dynamic tree mode
    ignoreQueryOnChildRequest: PropTypes.bool,

    // Event, that update records
    updateEvent: PropTypes.string,

    // Function, that render item data
    itemRenderer: PropTypes.func.isRequired,

    // Custom renderer for item button
    itemCustom: PropTypes.bool,

    // Full dotted tree path (like 1.2.3.4) of selected element
    selected: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string)
    ]),

    // Modify query, depends on parent node data, before send to backend
    queryModifier: PropTypes.func,

    // Data modifier function (for example - virtual grouping)
    dataModifier: PropTypes.func,

    shouldSelectNode: PropTypes.func,

    // Fires when node selected
    onSelect: PropTypes.func,

    // Dynamically load data when node opens
    loadOnDemand: PropTypes.bool,

    // Display request errors
    displayError: PropTypes.bool,

    // Display all tree nodes opened by default
    autoOpen: PropTypes.bool,

    // Fires after item is dragged and dropped
    onDrop: PropTypes.func,

    // Checks whether dropping item into current position is allowed in tree
    onDropAllowed: PropTypes.func,

    // Drag mode. Can be "move" or "copy".
    dragMode: PropTypes.string,

    // Do not update tree height (useful for tree inside modal)
    staticTreeHeight: PropTypes.bool,

    // Set Tree height
    treeHeight: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]),

    // Set Tree height
    rowHeight: PropTypes.number,

    // MultipleSelectionMode. Works properly with TreeContainer extended form component.
    multipleSelectionMode: PropTypes.bool,
    // List mode (Without open buttons)
    listMode: PropTypes.bool
  };

  static defaultProps = {
    providerParentCriteria: 'parent',
    idField: 'id',
    parentField: 'parent',
    childrenField: 'children',
    query: {},
    loadOnDemand: true,
    displayError: false,
    treeHeight: 'auto',
    rowHeight: 44,
    dragMode: 'move',
    listMode: false
  };

  constructor(props, ctx) {
    super(props, ctx);
    this._rowRenderer = this._rowRenderer.bind(this);
    this._loadOnDemand = this._loadOnDemand.bind(this);
    this._handleSelect = this._handleSelect.bind(this);
    this._handleShouldSelect = this._handleShouldSelect.bind(this);
    this._handleUpdateEvent = this._handleUpdateEvent.bind(this);
    this._afterUpdate = this._afterUpdate.bind(this);
    this._onHandleDrop = this._onHandleDrop.bind(this);
    this.updateTreeHeight = this.updateTreeHeight.bind(this);
    this.autoSelect = this.props.selected || null;

    this.state = {
      loading: false,
      loadError: false,
      errorMessage: null,
      treeHeight: props.treeHeight
    };

    this.onResize = !props.staticTreeHeight && this.updateTreeHeight;
  }

  componentDidMount() {
    if (!this.props.staticTreeHeight && this.treeContainer) {
      this.updateTreeHeight();
    }

    if (!this.props.data) {
      this._reloadData();
    }

    if (this.props.updateEvent) {
      subscribe(this.props.updateEvent, this._handleUpdateEvent);
    }
  }

  componentDidUpdate(prevProps) {
    // eslint-disable-next-line
    if (prevProps.selected != this.props.selected) {
      this.autoSelect = this.props.selected;
      this.tree.update();
    }

    if (
      !isEqual(this.props.query, prevProps.query) ||
      !isEqual(this.props.dataProvider, prevProps.dataProvider)) {
      this._reloadData();
    }
  }

  componentWillUnmount() {
    if (this.props.updateEvent) {
      unsubscribe(this.props.updateEvent, this._handleUpdateEvent);
    }
  }

  _processData(data, parentNode) {
    const { dataModifier, providerParentCriteria, loadOnDemand } = this.props;
    const newData = dataModifier ? dataModifier(data, parentNode) : data;

    each(newData, (val) => {
      val.$parent = val[providerParentCriteria]; // eslint-disable-line
      val.loadOnDemand = // eslint-disable-line
        typeof val.$loadOnDemand !== 'undefined' ? val.$loadOnDemand : loadOnDemand; // eslint-disable-line
      delete val[providerParentCriteria]; // eslint-disable-line
      if (val.children) {
        val.children = this._processData(val.children, val); // eslint-disable-line
      }
    });
    return newData;
  }

  _loadData(parent, parentNode) {
    const {
      query,
      dataProvider,
      providerParentCriteria,
      ignoreQueryOnChildRequest,
      queryModifier,
    } = this.props;

    let queryToSend = {};

    if (queryModifier) {
      queryToSend = queryModifier(parent, parentNode, query);
    } else {
      queryToSend = {
        ...((parent && ignoreQueryOnChildRequest) ? {} : query),
        [providerParentCriteria]: parent
      };
    }

    const dataProviderUrl = typeof dataProvider === 'function' ?
      dataProvider(parent, parentNode) : dataProvider;

    return new Promise((accept, reject) => {
      this.api.get(`data/${dataProviderUrl}`, {
        queryString: {
          json: JSON.stringify(queryToSend)
        }
      }).then((payload) => {
        this.tree.updateNode(parentNode, { $queryParent: parent });
        accept(this._processData(payload.data, parentNode));
      }).catch(error => reject(error));
    });
  }

  _rowRenderer({ node, tree }) {
    const { multipleSelectionMode, itemCustom, onDrop, titleField, listMode } = this.props;
    const { id, state = {}, $loader } = node;
    // const droppable = treeOptions.droppable;
    const { depth, open, selected = false } = state;

    const titleLoader = titleField ? node[titleField] : null;
    const label = $loader ? 'Загрузка...' : this.props.itemRenderer(node);
    const title = $loader ? 'Загрузка...' : titleLoader;

    return (
      <div
        draggable={!!onDrop}
        onDrop={this._onHandleDrop}
        onDragOver={e => e.preventDefault()}
        onDragStart={e => e.dataTransfer.setData('dragNodeId', node.id)}
        style={{ opacity: node.$removed ? 0.3 : 1 }}
        className={classNames(
          'infinite-tree-item',
          { 'infinite-tree-selected': selected }
        )}
        data-id={id}
      >
        <div
          className="infinite-tree-node"
          data-test="infiniteTreeNode"
          style={{ paddingLeft: depth * 16 }}
        >
          {multipleSelectionMode &&
            <Checkbox
              className="infinite-tree-checkbox"
              value={selected}
              onChange={() => {
                // eslint-disable-next-line
                node.state.selected = !node.state.selected;
                this._handleSelect(node, false);
              }}
              withIcon
              noError
            />
          }
          <Button
            className="infinite-tree-title"
            title={title}
            label={itemCustom ? null : label}
            onClick={() => {
              if (!multipleSelectionMode) {
                return tree.selectNode(node);
              }
              // eslint-disable-next-line
              node.state.selected = !node.state.selected;
              this._handleSelect(node, false);
            }}
            customRenderer={itemCustom}
            contentLeft
          >
            {itemCustom ?
              <span className="infinite-tree-title__btnContent">
                {label}
              </span> :
              null
            }
          </Button>
          {!$loader && !listMode &&
            <Button
              className={
                open ?
                  classNames(tree.options.togglerClass) :
                  classNames(tree.options.togglerClass, 'infinite-tree-closed')
              }
              iconSecondType={iconTypes.dropdown}
              onClick={() => {
                if (open) {
                  return tree.closeNode(node);
                }
                return tree.openNode(node);
              }}
              iconOnly
            />
          }
        </div>
      </div>
    );
  }

  _loadOnDemand(parentNode, done) {
    done(null, [{ $loader: true }]);
    this._loadData(parentNode[this.props.idField], parentNode)
      .then((data) => {
        this.tree.removeChildNodes(parentNode);
        done(null, data);
      });
  }

  _handleSelect(node, scrollToNode = true) {
    if (scrollToNode) this.tree.scrollToNode(node);
    if (this.props.onSelect) {
      this.props.onSelect({ ...node, parent: node.$parent }, !!this.autoSelect);
    }
  }

  _nodeById(id) {
    const nodes = this.tree.flattenChildNodes(
      this.tree.getRootNode()
    );
    let foundNode = null;
    each(nodes, (node) => {
      // eslint-disable-next-line
      if (node[this.props.idField] == id) {
        foundNode = node;
      }
    });
    return foundNode;
  }

  _findNodeData(data, id) {
    let found = null;
    each(data, (val) => {
      if (val[this.props.idField] === id) {
        found = val;
      }
      if (!found && val[this.props.childrenField]) {
        found = this._findNodeData(val[this.props.childrenField], id);
      }
    });
    return found;
  }

  _handleUpdateEvent(data = {}) {
    if (data.$action === 'update') {
      const node = this._nodeById(data[this.props.idField]);
      if (node) {
        const provider = typeof this.props.dataProvider === 'function' ?
          this.props.dataProvider(node.$parent, node) :
          this.props.dataProvider;

        const { queryModifier } = this.props;
        const queryToSend = queryModifier ?
          queryModifier(node.$parent, node.parent, this.props.query) : this.props.query;

        this.api.get(`data/${provider}`, {
          queryString: {
            json: JSON.stringify({
              ...queryToSend,
              [this.props.idField]: data[this.props.idField]
            })
          }
        }).then((payload) => {
          const found = this._findNodeData(payload.data, data[this.props.idField]);
          if (found) {
            this.tree.updateNode(node, { $removed: false, ...found });
          } else {
            this.tree.updateNode(node, { $removed: true });
          }
        });
      }
    }

    if (data.$action === 'insert' || data.$action === 'fullUpdate') {
      this._reloadData();
    }

    if (data.$action === 'delete') {
      const node = this._nodeById(data[this.props.idField]);
      if (node) {
        this.tree.removeNode(node);
      }
    }
  }

  _handleShouldSelect(node) {
    if (!node || (node === this.tree.getSelectedNode())) {
      return false; // Prevent from deselecting the current node
    }

    if (this.props.shouldSelectNode) {
      return this.props.shouldSelectNode(node);
    }
    return true;
  }

  /**
   * Reload tree
   * @private
   */
  _reloadData() {
    this.setState({
      loading: true,
      loadError: false
    });

    this._loadData(this.props.parent, this.tree.getRootNode())
      .then((data) => {
        this.setState({
          loading: false,
          loadError: false,
          errorMessage: null
        });
        this.tree.options.autoOpen = !!this.props.autoOpen;
        this.tree.loadData(data);
      }).catch((error) => {
        this.setState({
          loading: false,
          loadError: true,
          errorMessage: getErrorMessage(error)
        });
        throw new ApiError(error, { component: 'DynamicTree' });
      });
  }

  reload() {
    this._reloadData();
  }

  _chooseSelectionMode() {
    if (this.props.multipleSelectionMode) {
      const selectLength = this.autoSelect.length;
      each(this.autoSelect, (selIt, index) => {
        if (!selIt) return;

        this._selectNode(selIt, true);

        if (selectLength === index) this.autoSelect = null;
      });
    } else {
      this._selectNode(this.autoSelect);
    }
  }

  _selectNode(selectionStr, multiSelection = false) {
    const { idField } = this.props;
    const path = selectionStr.split('.');
    const last = path[path.length - 1];
    each(path, (val) => {
      const node = this.tree.getNodeById(val);
      if (node && node.parent && !node.parent.state.open) {
        this.tree.openNode(node.parent);
      }
      // eslint-disable-next-line
      if (node && node[idField] != last && !node.state.open) {
        this.tree.openNode(node);
      }
      // eslint-disable-next-line
      if (node && node[idField] == last) {
        if (!multiSelection) {
          this.tree.selectNode(node);
          this.tree.scrollToNode(node);
          setTimeout(() => this.autoSelect = null, 50);
        } else {
          // eslint-disable-next-line
          node.state.selected = true;
        }
      }
    });
  }

  _afterUpdate() {
    // Auto-select node
    if (this.autoSelect && !this.state.loading) {
      this._chooseSelectionMode();
    }
  }

  _onHandleDrop(e) {
    e.preventDefault();
    const dragNodeId = e.dataTransfer.getData('dragNodeId');
    const dropNodeId = e.currentTarget.getAttribute('data-id');
    if (dragNodeId && dropNodeId) {
      const { onDrop, onDropAllowed } = this.props;
      const draggedNode = this._nodeById(dragNodeId);
      const dropNode = this._nodeById(dropNodeId);

      if (onDropAllowed && onDropAllowed(draggedNode, dropNode) && onDrop) {
        onDrop(draggedNode, dropNode, () => {
          // const fromParent = draggedNode.parent;
          if (!dropNode.state.open) {
            this.tree.openNode(dropNode);
          }
          // Move node
          if (this.props.dragMode === 'move') {
            this.tree.moveNodeTo(draggedNode, dropNode);
            this._handleUpdateEvent({
              $action: 'update',
              [this.props.idField]: draggedNode[this.props.idField]
            });
          }
          if (this.props.dragMode === 'copy') {
            this._loadData(dropNode[this.props.idField], dropNode)
              .then((data) => {
                this.tree.removeChildNodes(dropNode);
                this.tree.addChildNodes(data, 0, dropNode);
                this.tree.openNode(dropNode);
              });
          }
        });
      }
    }
  }

  updateTreeHeight() {
    if (this.treeContainer) {
      if (__DEV_CLI__) {
        setTimeout(() => this.setState({ treeHeight: this.treeContainer.clientHeight }), 300);
        return;
      }
      this.setState({ treeHeight: this.treeContainer.clientHeight });
    }
  }

  render() {
    if (__SERVER__) {
      return (<div />);
    }

    const otherProps = {
      autoOpen: this.props.autoOpen
    };

    if (this.props.data) {
      otherProps.data = this.props.data;
    }

    return (
      <div
        {...classes({
          extra: this.props.className
        })}
        ref={ref => this.treeContainer = ref}
      >
        {this.state.loading &&
          <div>
            Загрузка...
          </div>
        }
        {this.state.loadError &&
          <div>
            Ошибка загрузки!
            {this.props.displayError &&
              <pre>
                {this.state.errorMessage}
              </pre>
            }
          </div>
        }
        <InfiniteTreePatched
          className="infinite-tree-content"
          ref={c => this.tree = c ? c.tree : this.tree}
          loadNodes={this._loadOnDemand}
          rowRenderer={this._rowRenderer}
          selectable
          shouldSelectNode={this._handleShouldSelect}
          onSelectNode={this._handleSelect}
          onContentDidUpdate={this._afterUpdate}
          height={this.state.treeHeight}
          rowHeight={this.props.rowHeight}
          {...otherProps}
        />
      </div>
    );
  }
}

class InfiniteTreePatched extends InfiniteTree {
  /**
   * Scroll patch
   */
  componentDidMount() {
    super.componentDidMount();
    this.tree.scrollToNode = (node) => {
      if (!this.tree || !this.virtualList) {
        return false;
      }

      const nodeIndex = this.tree.nodes.indexOf(node);
      if (nodeIndex < 0) {
        return false;
      }

      let offset = this.virtualList.getOffsetForIndex(nodeIndex);
      offset -= ((this.props.height / 2) - (this.props.rowHeight / 2));
      this.virtualList.scrollTo(offset);
      return true;
    };
  }
}

export default Tree;
